import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Observable, Subject, forkJoin } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { LoadingEntity } from '@inst-iot/bosch-angular-ui-components';
import { VehicleInfoService } from 'src/app/_services/vehicle-info.service';
import { VehicleGeneralFigureService } from 'src/app/_services/vehicle-general-figure.service';
import { NavigationService } from 'src/app/_services/navigation.service';
import { Vehicle, VehicleCurrentInfo } from 'src/app/_shared/models/vehicle';
import { sortRbTableData } from 'src/app/_shared/utils/sort-utils';
import { ALL_BATTERY_TYPES } from 'src/app/_shared/utils/battery-type';
import { PageData, PageChange, SortDirection } from '@inst-iot/bosch-angular-ui-components';

@Component({
  selector: 'app-vehicle-of-note',
  templateUrl: './vehicle-of-note.component.html',
  styleUrls: ['./vehicle-of-note.component.scss']
})
export class VehicleOfNoteComponent {
  private readonly unsubscribe$ = new Subject<void>();

  @Input() fleetId: string[] = [];
  @Input() batteryType: string[] = [];

  // Table state
  loader = new LoadingEntity();
  errorMessageTable: boolean = false;
  dataLoadingTable: boolean = false;
  vehiclesTable!: Vehicle[];
  deviceSerialNumberTable!: string[];
  vehiclesInfoTable!: { [key: string]: VehicleCurrentInfo };
  vehiclesList!: any[];
  vehiclesStressFactorTable!: { [key: string]: any };
  readonly sortData = sortRbTableData;
  // Pagination + search
  limit = 21;
  pageData = new PageData({ number: 0, size: this.limit, totalElements: 0, numberOfElements: 0 });
  searchQuery = '';
  private clientSearchActive = false;
  private readonly search$ = new Subject<string>();
  isPrefetchingAll = false;
  allVehiclesCache: Vehicle[] = [];
  sortField: string = 'agingClass';
  sortDirection: '' | 'asc' | 'desc' = 'asc';
  // cache for full list when searching
  filterVehiclesCache: Vehicle[] = [];

  constructor(
    private readonly navigationService: NavigationService,
    private readonly vehicleInfoService: VehicleInfoService,
    private readonly vehicleGeneralFigureService: VehicleGeneralFigureService
  ) {}

  ngOnInit(): void {
    this.dataLoadingTable = true;
    this.errorMessageTable = false;
    // load first page (skipped when a battery type filter is active: the API does not
    // support batteryType, so the list is built client-side from the prefetched cache)
    if (this.batteryTypeFilter.length === 0) {
      this.loadPage(1);
    }

    // setup debounced search handling
    this.search$.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.unsubscribe$)).subscribe(() => {
      this.pageData = new PageData({ ...this.pageData, number: 0 });
      if (!this.allVehiclesCache || this.allVehiclesCache.length === 0) {
        this.retrieveAllVehiclesForNote();
      } else {
        this.applyClientSideFilterSort();
      }
    });

    // background prefetch of full list to support global sort/search
    this.retrieveAllVehiclesForNote();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  addCurrentDataToVehicleTable() {
    this.vehiclesList = this.vehiclesTable.map(vehicle => {
      const info = this.vehiclesInfoTable[vehicle.batteryIdentifier];
      return {
        ...vehicle,
        ...(info ? info : {})
      };
    });
  }

  addStressFactorsDataToVehicleTable() {
    this.vehiclesList = this.vehiclesList.map(vehicle => {
      const stressArray = this.vehiclesStressFactorTable[vehicle.batteryIdentifier]?.stressFactors;
      const legacy = this.vehiclesStressFactorTable[vehicle.batteryIdentifier];
      let stressFactors: any;
      if (Array.isArray(stressArray)) {
        stressFactors = stressArray;
      } else if (legacy) {
        stressFactors = legacy;
      }

      return {
        ...vehicle,
        stressFactors
      };
    });
    this.dataLoadingTable = false;
    // hasVehiclesOfNote is exposed as a getter below
  }

  // Prefetch all pages into a client-side cache so sorting/search act on full dataset
  retrieveAllVehiclesForNote() {
    if (this.isPrefetchingAll) return;
    this.isPrefetchingAll = true;
    this.vehicleInfoService.getVehiclesWithPagination(1, this.limit, this.fleetId).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: firstResp => {
        const total = firstResp.total || (firstResp.data ? firstResp.data.length : 0);
        this.allVehiclesCache = firstResp.data || [];
        const pages = Math.ceil(total / this.limit);
        const requests: any[] = [];
        for (let p = 2; p <= pages; p++) {
          requests.push(this.vehicleInfoService.getVehiclesWithPagination(p, this.limit, this.fleetId));
        }
        if (requests.length === 0) {
          this.isPrefetchingAll = false;
          // also prefetch current info for cached vehicles in batches
          this.prefetchCurrentInfoForCache();
          return;
        }
        forkJoin(requests).pipe(takeUntil(this.unsubscribe$)).subscribe(resArray => {
          const rest = resArray.flatMap((r: any) => r.data || []);
          this.allVehiclesCache = (firstResp.data || []).concat(rest);
          this.isPrefetchingAll = false;
          this.prefetchCurrentInfoForCache();
        }, err => {
          console.error('Error prefetching vehicle pages for note', err);
          this.isPrefetchingAll = false;
        });
      },
      error: err => {
        console.error('Error fetching first page for prefetch in vehicle-of-note', err);
        this.isPrefetchingAll = false;
      }
    });
  }

  private prefetchCurrentInfoForCache(batchSize = 50) {
    const ids = (this.allVehiclesCache || []).map(v => v.batteryIdentifier).filter(Boolean);
    if (!ids || ids.length === 0) return;
    const requests: any[] = [];
    for (let i = 0; i < ids.length; i += batchSize) {
      const chunk = ids.slice(i, i + batchSize);
      requests.push(this.vehicleInfoService.getCurrentVehicleInfo(chunk));
    }
    if (requests.length === 0) return;
    forkJoin(requests).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: resArray => {
        this.vehiclesInfoTable = this.vehiclesInfoTable || {};
        resArray.forEach((r: any) => { this.vehiclesInfoTable = { ...this.vehiclesInfoTable, ...(r || {}) }; });
      },
      error: err => {
        console.error('Error prefetching current vehicle info for cache', err);
        this.reapplyActiveClientState();
      },
      complete: () => this.reapplyActiveClientState()
    });
  }

  private reapplyActiveClientState(): void {
    if (this.sortField || (this.searchQuery || '').trim()) {
      this.applyClientSideFilterSort();
    }
  }

  private loadStressFactorsForPage(pageSlice: any[], ids: string[]): void {
    this.vehicleGeneralFigureService.getStressFactors(ids).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: sf => { this.vehiclesStressFactorTable = { ...(this.vehiclesStressFactorTable || {}), ...sf }; },
      error: err => console.error('Error fetching stress factors for page', err),
      complete: () => this.finalizeVehiclePage(pageSlice)
    });
  }

  private loadStressFactorsForSearchPage(pageSlice: any[], ids: string[]): void {
    this.vehicleGeneralFigureService.getStressFactors(ids).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: sf => { this.vehiclesStressFactorTable = { ...(this.vehiclesStressFactorTable || {}), ...sf }; },
      error: err => console.error('Error fetching stress factors for search page', err),
      complete: () => this.finalizeVehiclePage(pageSlice)
    });
  }

  private finalizeVehiclePage(pageSlice: any[]): void {
    this.vehiclesList = this.buildVehicleList(pageSlice);
    this.dataLoadingTable = false;
  }

  private buildVehicleList(vehicles: any[]): any[] {
    return vehicles.map(vehicle => this.buildVehicleRow(vehicle));
  }

  private buildVehicleRow(vehicle: any): any {
    return {
      ...vehicle,
      ...(this.vehiclesInfoTable[vehicle.batteryIdentifier] || {}),
      stressFactors: this.vehiclesStressFactorTable[vehicle.batteryIdentifier]
    };
  }

  private loadStressFactorsForTable(stressObs: Observable<any>): void {
    stressObs.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: sf => { this.vehiclesStressFactorTable = { ...(this.vehiclesStressFactorTable || {}), ...sf }; },
      error: err => { console.error('Error fetching stress factors for page', err); },
      complete: () => this.finalizeVehicleTable()
    });
  }

  private finalizeVehicleTable(): void {
    this.vehiclesList = this.buildVehicleList(this.vehiclesTable || []);
    this.dataLoadingTable = false;
  }

  onSearchChange(value: string) {
    this.searchQuery = value;
    this.search$.next(value || '');
  }

  onSortChange(event: { id: string, direction: string }) {
    this.sortField = event.id || '';
    this.sortDirection = (event.direction as '' | 'asc' | 'desc') || '';
    this.pageData = new PageData({ ...this.pageData, number: 0 });
    if (!this.allVehiclesCache || this.allVehiclesCache.length === 0) {
      this.retrieveAllVehiclesForNote();
    } else {
      this.applyClientSideFilterSort();
    }
  }

  private compareAgingClass(a: any, b: any, direction: SortDirection): number {
    const agingOrder: { [key: string]: number } = {
      'below average': 0,
      'average': 1,
      'above average': 2
    };
    const aValue = a?.agingClass?.trim().toLowerCase();
    const bValue = b?.agingClass?.trim().toLowerCase();
    const aRank = agingOrder[aValue];
    const bRank = agingOrder[bValue];

    // Missing or unexpected values are always placed after the known conditions.
    if (aRank === undefined && bRank === undefined) return 0;
    if (aRank === undefined) return 1;
    if (bRank === undefined) return -1;

    return (aRank - bRank) * (direction === 'asc' ? 1 : -1);
  }

  // The vehicles API has no batteryType query parameter, so the filter is applied client-side.
  // `all_battery_types` means "no filter": no vehicle carries that value.
  private get batteryTypeFilter(): string[] {
    return this.batteryType.filter((type) => type !== ALL_BATTERY_TYPES);
  }

  private filterByBatteryType(vehicles: Vehicle[]): Vehicle[] {
    const filter = this.batteryTypeFilter;
    if (filter.length === 0) return vehicles;
    return vehicles.filter(v => filter.includes(v.batteryType));
  }

  applyClientSideFilterSort() {
    const q = (this.searchQuery || '').trim().toLowerCase();
    // merge cached vehicles with any prefetched current info so sort can use numeric fields
    const merged = this.filterByBatteryType(this.allVehiclesCache || []).map(v => ({ ...v, ...(this.vehiclesInfoTable?.[v.batteryIdentifier] || {}) }));
    let list = [...merged];
    if (q) {
      list = list.filter(v =>
        (v.name || '').toString().toLowerCase().includes(q) ||
        (v.batteryIdentifier || '').toString().toLowerCase().includes(q) ||
        (v.vehicleModel || '').toString().toLowerCase().includes(q) ||
        (v.vehicleBrand || '').toString().toLowerCase().includes(q)
      );
    }
    if (this.sortField) {
      const compareFn = this.sortField === 'agingClass' ? this.compareAgingClass : undefined;
      list = sortRbTableData({ id: this.sortField, direction: this.sortDirection as any }, list, undefined, compareFn);
    }
    const start = (this.pageData.number || 0) * (this.pageData.size || this.limit);
    const pageSize = this.pageData.size || this.limit;
    const pageSlice = list.slice(start, start + pageSize);
    this.pageData = new PageData({ number: this.pageData.number, size: pageSize, totalElements: list.length, numberOfElements: pageSlice.length });
    const ids = pageSlice.map((v: any) => v.batteryIdentifier).filter(Boolean);
    if (ids.length > 0) {
      // ensure we have current info for these ids (might have been prefetched)
      const missing = ids.filter(id => !this.vehiclesInfoTable || !this.vehiclesInfoTable[id]);
      if (missing.length > 0) {
        this.vehicleInfoService.getCurrentVehicleInfo(missing).pipe(takeUntil(this.unsubscribe$)).subscribe({
          next: info => { this.vehiclesInfoTable = { ...(this.vehiclesInfoTable || {}), ...info }; },
          error: e => { console.error('Error fetching current vehicle info for search page', e); },
          complete: () => this.loadStressFactorsForPage(pageSlice, ids)
        });
      } else {
        // we have info, just fetch stress factors
        this.loadStressFactorsForPage(pageSlice, ids);
      }
    } else {
      this.vehiclesList = [];
      this.dataLoadingTable = false;
    }
  }

  getStressLevel(vehicle: any, stressName: string): number | null {
    const arr = Array.isArray(vehicle?.stressFactors)
      ? vehicle.stressFactors
      : this.vehiclesStressFactorTable?.[vehicle.batteryIdentifier]?.stressFactors;
    if (!Array.isArray(arr)) return null;
    const found = arr.find((s: any) => s.stressFactorName === stressName);
    return found ? found.level : null;
  }

  getStressText(vehicle: any, stressName: string): string | null {
    const arr = Array.isArray(vehicle?.stressFactors)
      ? vehicle.stressFactors
      : this.vehiclesStressFactorTable?.[vehicle.batteryIdentifier]?.stressFactors;
    if (!Array.isArray(arr)) return null;
    const found = arr.find((s: any) => s.stressFactorName === stressName);
    return found ? found.text : null;
  }

  goToInsights(vehicleId: string) {
    this.navigationService.navigateTo('insights', vehicleId);
  }

  // expose property used by template
  get hasVehiclesOfNote(): boolean {
    return this.vehiclesList?.some(vehicle => {
      const checks = [
        this.getStressLevel(vehicle, 'Low Temperature Charging'),
        this.getStressLevel(vehicle, 'Fast Charging'),
        this.getStressLevel(vehicle, 'High State of Charge'),
        this.getStressLevel(vehicle, 'Low State of Charge'),
        this.getStressLevel(vehicle, 'High Temperature')
      ];
      return checks.some(level => typeof level === 'number' && level > 1);
    });
  }

  // pagination + search helpers
  navigateCall(pageChange: PageChange) {
    const page = pageChange.number + 1;
    this.pageData = new PageData({ ...this.pageData, number: pageChange.number });
    // a battery type filter can only be applied client-side
    if (this.batteryTypeFilter.length > 0) {
      this.applyClientSideFilterSort();
      return;
    }
    // if searching, handle client-side pagination
    if (this.searchQuery && this.searchQuery.trim() !== '') {
      this.applyClientSideFilterSort();
      return;
    }
    // if we have an active client-side sort, apply it across pages
    if (this.sortField && this.sortField !== '') {
      this.applyClientSideFilterSort();
      return;
    }
    this.loadPage(page);
  }

  applySearchAndFilters() {
    const q = (this.searchQuery || '').toLowerCase().trim();
    if (q !== '') {
      this.clientSearchActive = true;
      this.dataLoadingTable = true;
      // load full list then filter client-side
      this.vehicleInfoService.getVehiclesWithPagination(1, 0, this.fleetId).pipe(takeUntil(this.unsubscribe$)).subscribe({
        next: res => {
          this.filterVehiclesCache = this.filterByBatteryType(res.data || []);
          // filter
          const filtered = (this.filterVehiclesCache || []).filter(v =>
            (v.name || '').toString().toLowerCase().includes(q) ||
            (v.batteryIdentifier || '').toString().toLowerCase().includes(q) ||
            (v.vehicleModel || '').toString().toLowerCase().includes(q) ||
            (v.vehicleBrand || '').toString().toLowerCase().includes(q)
          );
          const total = filtered.length;
          const pageSize = this.pageData.size || this.limit;
          const start = (this.pageData.number || 0) * pageSize;
          const pageSlice = filtered.slice(start, start + pageSize);
          this.pageData = new PageData({ number: this.pageData.number, size: pageSize, totalElements: total, numberOfElements: pageSlice.length });
          const ids = pageSlice.map((v: any) => v.batteryIdentifier).filter(Boolean);
          if (ids.length > 0) {
            this.vehicleInfoService.getCurrentVehicleInfo(ids).pipe(takeUntil(this.unsubscribe$)).subscribe({
              next: info => {
                this.vehiclesInfoTable = { ...(this.vehiclesInfoTable || {}), ...info };
                this.vehiclesStressFactorTable = this.vehiclesStressFactorTable || {};
                this.loadStressFactorsForSearchPage(pageSlice, ids);
              },
              error: e => {
                console.error('Error fetching current vehicle info for search page', e);
                this.vehiclesList = pageSlice;
                this.dataLoadingTable = false;
              }
            });
          } else {
            this.vehiclesList = [];
            this.dataLoadingTable = false;
          }
        },
        error: err => { console.error('Error loading full list for search', err); this.dataLoadingTable = false; }
      });
      return;
    }
    // clear search
    if (this.clientSearchActive) {
      this.clientSearchActive = false;
      this.pageData = new PageData({ number: 0, size: this.limit, totalElements: 0, numberOfElements: 0 });
      this.loadPage(1);
    }
  }

  private loadPage(page: number) {
    this.dataLoadingTable = true;
    this.errorMessageTable = false;
    const pageSize = this.pageData.size || this.limit;
    this.vehicleInfoService.getVehiclesWithPagination(page, pageSize, this.fleetId).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: res => {
        this.vehiclesTable = res.data || [];
        this.pageData = new PageData({ number: page - 1, size: pageSize, totalElements: res.total ?? 0, numberOfElements: (res.data || []).length });
      },
      error: err => {
        console.error('Error fetching Vehicles page:', err);
        this.dataLoadingTable = false;
        this.errorMessageTable = true;
      },
      complete: () => {
        const ids = (this.vehiclesTable || []).map(v => v.batteryIdentifier).filter(Boolean);
        if (ids.length > 0) {
          const missing = ids.filter(id => !this.vehiclesInfoTable || !this.vehiclesInfoTable[id]);
          const infoObs = missing.length > 0 ? this.vehicleInfoService.getCurrentVehicleInfo(missing) : null;
          const stressObs = this.vehicleGeneralFigureService.getStressFactors(ids);
          // fetch current info for missing and stress factors for this page
          if (infoObs) {
            infoObs.pipe(takeUntil(this.unsubscribe$)).subscribe({
              next: info => { this.vehiclesInfoTable = { ...(this.vehiclesInfoTable || {}), ...info }; },
              error: e => { console.error('Error fetching current vehicle info for page', e); },
              complete: () => this.loadStressFactorsForTable(stressObs)
            });
          } else {
            this.loadStressFactorsForTable(stressObs);
          }
        } else {
          this.vehiclesList = [];
          this.dataLoadingTable = false;
        }
      }
    });
  }
}
